import type {
  MyProductReview,
  MyProductReviewAnswer,
  PRODUCT_REVIEW_LIMITS,
  PRODUCT_REVIEW_ROLES,
  ProductReviewRating,
  ProductReviewRole,
  ProductReviewStatus,
} from '../../../../../music24-api/src/productReviews/productReviewTypes';

/*
 * The pure rules of the review form (design review-form a1, a2). The web app does not import values
 * from music24-api, so the limits and the roles are copies. The `typeof` types make the compiler
 * refuse a copy that differs from `productReviewTypes.ts`.
 */

/** Mirrors `PRODUCT_REVIEW_LIMITS` in `music24-api/src/productReviews/productReviewTypes.ts`. */
export const REVIEW_LIMITS: typeof PRODUCT_REVIEW_LIMITS = {
  authorNameMin: 1,
  authorNameMax: 80,
  titleMax: 120,
  bodyMin: 40,
  bodyMax: 2000,
};

/** Mirrors `PRODUCT_REVIEW_ROLES`, in the order of the select. */
export const REVIEW_ROLES: typeof PRODUCT_REVIEW_ROLES = ['artist', 'manager', 'label', 'other'];

/** The places that open the form, as in `review_form_opened`. The native apps send `settings`. */
const REVIEW_FROM_VALUES = ['menu', 'settings'] as const;

export type ReviewFrom = (typeof REVIEW_FROM_VALUES)[number];

/** A link is free text, so an unknown `from` counts as the menu and never reaches an event. */
export function reviewFromOf(value: string | null): ReviewFrom {
  return REVIEW_FROM_VALUES.find((known) => known === value) ?? 'menu';
}

/** The values of the form. `rating` is 0 before the user picks a star. */
export interface ReviewDraft {
  rating: number;
  authorName: string;
  authorRole: ProductReviewRole;
  title: string;
  body: string;
  consentToPublish: boolean;
}

export type ReviewField = 'rating' | 'authorName' | 'title' | 'body' | 'consentToPublish';

/** One problem of a field. `bodyShort` carries the characters that are missing. */
export type ReviewProblem =
  | { field: 'rating'; kind: 'missing' }
  | { field: 'authorName'; kind: 'missing' | 'tooLong' }
  | { field: 'title'; kind: 'tooLong' }
  | { field: 'body'; kind: 'tooShort'; missing: number }
  | { field: 'body'; kind: 'tooLong' }
  | { field: 'consentToPublish'; kind: 'missing' };

/** The `reviews.submit` input. */
export interface ReviewSubmitInput {
  authorName: string;
  authorRole: ProductReviewRole;
  rating: ProductReviewRating;
  title: string | null;
  body: string;
  consentToPublish: true;
}

/** The form values for a user with no review, or the saved values of the review. */
export function draftFromAnswer(answer: MyProductReviewAnswer): ReviewDraft {
  const { review } = answer;
  if (review === null) {
    return {
      rating: 0,
      authorName: answer.defaultAuthorName.slice(0, REVIEW_LIMITS.authorNameMax),
      authorRole: 'artist',
      title: '',
      body: '',
      consentToPublish: false,
    };
  }
  return {
    rating: review.rating,
    authorName: review.authorName,
    authorRole: review.authorRole,
    title: review.title ?? '',
    body: review.body,
    consentToPublish: review.consentToPublish,
  };
}

/** The length the counter shows: the server checks the trimmed text. */
export function bodyLength(body: string): number {
  return body.trim().length;
}

function isRating(value: number): value is ProductReviewRating {
  return Number.isInteger(value) && value >= 1 && value <= 5;
}

/** Every problem of the draft, in the order of the form. An empty list means the form can go. */
export function validateReviewDraft(draft: ReviewDraft): ReviewProblem[] {
  const problems: ReviewProblem[] = [];
  if (!isRating(draft.rating)) {
    problems.push({ field: 'rating', kind: 'missing' });
  }
  const name = draft.authorName.trim();
  if (name.length < REVIEW_LIMITS.authorNameMin) {
    problems.push({ field: 'authorName', kind: 'missing' });
  } else if (name.length > REVIEW_LIMITS.authorNameMax) {
    problems.push({ field: 'authorName', kind: 'tooLong' });
  }
  if (draft.title.trim().length > REVIEW_LIMITS.titleMax) {
    problems.push({ field: 'title', kind: 'tooLong' });
  }
  const length = bodyLength(draft.body);
  if (length < REVIEW_LIMITS.bodyMin) {
    problems.push({ field: 'body', kind: 'tooShort', missing: REVIEW_LIMITS.bodyMin - length });
  } else if (length > REVIEW_LIMITS.bodyMax) {
    problems.push({ field: 'body', kind: 'tooLong' });
  }
  if (!draft.consentToPublish) {
    problems.push({ field: 'consentToPublish', kind: 'missing' });
  }
  return problems;
}

/** The problem of one field, or undefined. */
export function problemOf(problems: ReviewProblem[], field: ReviewField): ReviewProblem | undefined {
  return problems.find((problem) => problem.field === field);
}

/** The `reviews.submit` input of a valid draft, or null when the draft has a problem. */
export function toSubmitInput(draft: ReviewDraft): ReviewSubmitInput | null {
  if (validateReviewDraft(draft).length > 0 || !isRating(draft.rating)) {
    return null;
  }
  const title = draft.title.trim();
  return {
    authorName: draft.authorName.trim(),
    authorRole: draft.authorRole,
    rating: draft.rating,
    title: title === '' ? null : title,
    body: draft.body.trim(),
    consentToPublish: true,
  };
}

/** The status chip of a saved review (design a7, a8, a9). */
export type ReviewStatusView = 'inReview' | 'published' | 'notPublished';

export function statusViewOf(status: ProductReviewStatus): ReviewStatusView {
  if (status === 'APPROVED') {
    return 'published';
  }
  if (status === 'REJECTED') {
    return 'notPublished';
  }
  return 'inReview';
}

/** True when a saved edit sends a published review back to the team (server rule). */
export function editGoesBackToReview(review: MyProductReview | null): boolean {
  return review !== null && review.status === 'APPROVED';
}
